/**
 * AboutPage コンポーネント
 *
 * サイトの概要、利用上の注意、謝辞を表示するページ。
 */

import { motion, useReducedMotion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Trans } from 'react-i18next';
import { Header } from '@/components/organisms/Header';
import { Footer } from '@/components/organisms/Footer';
import { useI18n } from '@/i18n/useI18n';

/**
 * AboutPage コンポーネント
 *
 * 使用例:
 * ```tsx
 * <AboutPage />
 * ```
 */
export function AboutPage() {
  const { t } = useI18n();
  const shouldReduceMotion = useReducedMotion();

  return (
    <div className="flex min-h-screen flex-col">
      <Helmet>
        <title>
          {t('aboutPage.title')} | {t('site.title')}
        </title>
        <meta name="description" content={t('aboutPage.intro.description')} />
      </Helmet>

      <Header />

      <main id="main-content" className="flex-1">
        {/* ヘッダーセクション */}
        <section className="border-b bg-gradient-to-b from-background to-muted/20 py-12">
          <div className="container mx-auto px-4">
            <motion.div
              initial={shouldReduceMotion ? false : { opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
                {t('aboutPage.title')}
              </h1>
            </motion.div>
          </div>
        </section>

        {/* コンテンツセクション */}
        <section className="py-12">
          <div className="container mx-auto max-w-4xl px-4">
            <div className="space-y-12">
              {/* 概要 */}
              <motion.div
                initial={shouldReduceMotion ? false : { opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="space-y-4"
              >
                <h2 className="text-2xl font-bold tracking-tight">
                  {t('aboutPage.intro.heading')}
                </h2>
                <div className="space-y-3 text-muted-foreground">
                  <p>{t('aboutPage.intro.description')}</p>
                  <p>
                    <Trans
                      i18nKey="aboutPage.intro.funding"
                      components={[
                        // eslint-disable-next-line jsx-a11y/anchor-has-content
                        <a
                          key="link"
                          href={t('aboutPage.intro.originalSiteUrl')}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                        />,
                      ]}
                    />
                  </p>
                  <p>{t('aboutPage.intro.dataSource')}</p>
                </div>
              </motion.div>

              {/* 区切り線 */}
              <div className="border-t" />

              {/* ご利用にあたって */}
              <motion.div
                initial={shouldReduceMotion ? false : { opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="space-y-4"
              >
                <h2 className="text-2xl font-bold tracking-tight">
                  {t('aboutPage.notes.heading')}
                </h2>
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex gap-2">
                    <span className="text-foreground">•</span>
                    <span>{t('aboutPage.notes.remarks')}</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-foreground">•</span>
                    <span>{t('aboutPage.notes.thumbnails')}</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-foreground">•</span>
                    <span>{t('aboutPage.notes.copyright')}</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-foreground">•</span>
                    <span>{t('aboutPage.notes.metadataLicense')}</span>
                  </li>
                </ul>
              </motion.div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
